import Vue from 'vue';
import VueRouter from 'vue-router';
import VueStore from '@/store';
import { handleAuthenticatedUserAccess } from '@/router/routerUtils';
import AdminRoute from '@/router/routes/admin.route';
import ClientRoute from '@/router/routes/client.route';
import StaticRoute from '@/router/routes/static.route';
import api from '@/api';
import * as Sentry from '@sentry/vue';
import { isUuid } from '@/util/uuid';

Vue.use(VueRouter);

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [...AdminRoute, ...ClientRoute, ...StaticRoute],
});

router.beforeEach(async (to, from, next) => {
  await VueStore.dispatch('routes/setLoading', true);
  const isUserLogged = VueStore.getters['user/isLogged'];

  // Guard: if we ever land on `/brand/<non-uuid>/...` (e.g. `/brand/false/...`),
  // it usually comes from a stale session/localStorage and leads to `/brands/false` API calls.
  // The safest behavior is to force a clean login.
  try {
    if (typeof to?.path === 'string' && to.path.startsWith('/brand/')) {
      const pathBrandId = to.path.split('/')[2];
      const routeBrandId = to?.params?.id ?? pathBrandId;
      if (routeBrandId && !isUuid(routeBrandId)) {
        if (process.env.VUE_APP_SENTRY_DSN) {
          Sentry.withScope((scope) => {
            scope.setLevel('warning');
            scope.setTag(
              'errorType',
              'invalid_brand_id_in_route_forced_logout',
            );
            scope.setExtra('routeBrandId', routeBrandId);
            scope.setExtra('toFullPath', to?.fullPath);
            scope.setExtra('isUserLogged', isUserLogged);
            Sentry.captureMessage(
              'Invalid brand id in route - forcing logout and redirect to login',
            );
          });
        }

        try {
          await api.post('/users/logout');
        } catch (error) {
          if (process.env.VUE_APP_SENTRY_DSN) {
            Sentry.captureException(error);
          }
        }

        try {
          localStorage.removeItem('objekto');
        } catch (error) {
          if (process.env.VUE_APP_SENTRY_DSN) {
            Sentry.captureException(error);
          }
        }

        try {
          await VueStore.dispatch('user/clearState');
        } catch (error) {
          if (process.env.VUE_APP_SENTRY_DSN) {
            Sentry.captureException(error);
          }
        }

        return next({ name: 'Login' });
      }
    }
  } catch (error) {
    if (process.env.VUE_APP_SENTRY_DSN) {
      Sentry.captureException(error);
    }
  }

  for (const matchedRoute of to.matched) {
    if (matchedRoute.meta.isAuthForbidden === true) {
      return isUserLogged ? next('/') : next();
    }

    if (matchedRoute.meta.isAuthRequired === true) {
      if (!isUserLogged) {
        try {
          const response = await api.get('/users/profile');
          if (response.data) {
            await VueStore.dispatch('user/setUser', {
              data: response.data,
              noRedirect: true,
            });
            return handleAuthenticatedUserAccess(to, from, next);
          }
        } catch (error) {
          return next({ name: 'Login', query: { redirect: to.fullPath } });
        }
      }
      return handleAuthenticatedUserAccess(to, from, next);
    }
  }

  if (to.path === '/') {
    if (isUserLogged) {
      const initialRoute = VueStore.getters['user/initialRouteName'];
      const userBrand = VueStore.getters['user/brand'];

      return next({ name: initialRoute(), params: { id: userBrand } });
    } else {
      return next({ name: 'Login' });
    }
  }

  return next();
});

router.afterEach(async () => {
  await VueStore.dispatch('routes/setLoading', false);
});

export default router;
